import React, { useState, useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { API_BASE_URL } from '../../config';

function BasicInfoStep({ formData, handleInputChange, focusedField, isAnimating, handlePrevStep, handleNextStep, isReadOnly }) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const dataFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch data once
    if (dataFetchedRef.current) return;

    const fetchBasicInfo = async () => {
      try {
        setIsLoading(true);
        setApiError(null);
        
        // Get auth details from localStorage or context
        const auth_key = localStorage.getItem('authKey');
        const user_id = localStorage.getItem('userId');

        if (!auth_key || !user_id) {
          throw new Error('Authentication credentials not found. Please log in again.');
        }

        const response = await fetch(`${API_BASE_URL}/brands/onboarding/basic-info/fetch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ auth_key, user_id }),
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          // Update form data with fetched information
          if (data.basic_info) {
            // Call the parent component's handleInputChange with each field
            Object.entries(data.basic_info).forEach(([key, value]) => {
              if (value && formData.hasOwnProperty(key)) {
                handleInputChange({
                  target: {
                    name: key,
                    value: value
                  }
                }, { isFromApi: true });
              }
            });
          }
        } else {
          // If 404 is returned, it means basic info doesn't exist yet which is fine
          if (response.status !== 404) {
            throw new Error(data.message || 'Failed to fetch basic information');
          }
        }
        
        // Mark as fetched to prevent further fetches
        dataFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching basic info:', error);
        setApiError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBasicInfo();
  }, [formData, handleInputChange]);  // These deps are fine now with the ref check

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateName = (name) => {
    return name.length >= 2;
  };

  const validatePhone = (phone) => {
    return phone && phone.length >= 10;
  };

  const handlePhoneChange = (value, data, field) => {
    handleInputChange({
      target: {
        name: field,
        value: value
      }
    });
  };

  const handleSaveProgress = async () => {
    if (isReadOnly) return;
    
    setIsSubmitting(true);
    setSaveSuccess(false);
    setApiError(null);

    try {
      // Get auth details from localStorage or context
      const auth_key = localStorage.getItem('auth_key');
      const user_id = localStorage.getItem('user_id');

      if (!auth_key || !user_id) {
        throw new Error('Authentication credentials not found. Please log in again.');
      }

      const payload = {
        ...formData,
        auth_key,
        user_id
      };

      const response = await fetch(`${API_BASE_URL}/brands/onboarding/basic-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }

      // If successful, show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000); // Hide after 3 seconds
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (isReadOnly) {
      handleNextStep();
      return;
    }
    
    const newErrors = {};

    // Validate organization email
    if (!validateEmail(formData.organizationEmail)) {
      newErrors.organizationEmail = 'Please enter a valid organization email';
    }

    // Validate primary contact details
    if (!validateName(formData.primaryContactName)) {
      newErrors.primaryContactName = 'Name should be at least 2 characters long';
    }
    if (!validatePhone(formData.primaryContactPhone)) {
      newErrors.primaryContactPhone = 'Please enter a valid phone number';
    }
    if (!validateEmail(formData.primaryContactEmail)) {
      newErrors.primaryContactEmail = 'Please enter a valid email address';
    }

    // Validate business owner details
    if (!validateName(formData.businessOwnerName)) {
      newErrors.businessOwnerName = 'Name should be at least 2 characters long';
    }
    if (!validatePhone(formData.ownerContactNumber)) {
      newErrors.ownerContactNumber = 'Please enter a valid phone number';
    }
    if (!validateEmail(formData.ownerEmail)) {
      newErrors.ownerEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      setApiError(null);

      try {
        // Get auth details from localStorage or context
        const auth_key = localStorage.getItem('authKey');
        const user_id = localStorage.getItem('userId');

        if (!auth_key || !user_id) {
          throw new Error('Authentication credentials not found. Please log in again.');
        }

        const payload = {
          ...formData,
          auth_key,
          user_id
        };

        const response = await fetch(`${API_BASE_URL}/brands/onboarding/basic-info`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        // If successful, move to next step
        handleNextStep();
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="onboarding-section basic-info">
      <div className="basic-container">
        <h1 className="gstin-title">Basic Information</h1>

        {apiError && (
          <div className="api-error-message">
            {apiError}
          </div>
        )}

        {saveSuccess && (
          <div className="success-message">
            Your progress has been saved successfully!
          </div>
        )}

        {isLoading ? (
          <div className="loading-state">
            <p>Loading your information...</p>
          </div>
        ) : (
          <>
            <section className="contact-information">
              <h2 className="subsection-title">Contact Information</h2>
              <div className="form-row">
                <div className={`form-group ${focusedField === 'organizationEmail' ? 'focused' : ''}`}>
                  <label>Organization Email ID<span className="required"></span></label>
                  <input
                    type="email"
                    name="organizationEmail"
                    value={formData.organizationEmail}
                    onChange={handleInputChange}
                    placeholder="Organization Email ID"
                    readOnly={isReadOnly}
                  />
                  <span className="field-note">Common Email ID for login</span>
                  {errors.organizationEmail && <span className="error-message">{errors.organizationEmail}</span>}
                </div>
                <div className={`form-group ${focusedField === 'primaryContactName' ? 'focused' : ''}`}>
                  <label>Primary Contact's Name<span className="required"></span></label>
                  <input
                    type="text"
                    name="primaryContactName"
                    value={formData.primaryContactName}
                    onChange={handleInputChange}
                    placeholder="Primary Contact's Name"
                    readOnly={isReadOnly}
                  />
                  <span className="field-note">Person responsible for admin operations</span>
                  {errors.primaryContactName && <span className="error-message">{errors.primaryContactName}</span>}
                </div>
                <div className={`form-group ${focusedField === 'primaryContactPhone' ? 'focused' : ''}`}>
                  <label>Primary Contact's Phone Number<span className="required"></span></label>
                  <PhoneInput
                    country={'in'}
                    value={formData.primaryContactPhone}
                    onChange={(value, data) => handlePhoneChange(value, data, 'primaryContactPhone')}
                    inputProps={{
                      name: 'primaryContactPhone',
                      required: true,
                      placeholder: 'Enter phone number',
                      readOnly: isReadOnly
                    }}
                    disabled={isReadOnly}
                  />
                  {errors.primaryContactPhone && <span className="error-message">{errors.primaryContactPhone}</span>}
                </div>
                <div className={`form-group ${focusedField === 'primaryContactEmail' ? 'focused' : ''}`}>
                  <label>Primary Contact's Email ID<span className="required"></span></label>
                  <input
                    type="email"
                    name="primaryContactEmail"
                    value={formData.primaryContactEmail}
                    onChange={handleInputChange}
                    placeholder="Primary Contact's Email ID"
                    required
                    readOnly={isReadOnly}
                  />
                  <span className="field-note">Please provide your Email ID</span>
                  {errors.primaryContactEmail && <span className="error-message">{errors.primaryContactEmail}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className={`form-group ${focusedField === 'businessOwnerName' ? 'focused' : ''}`}>
                  <label>Business Owner's Name<span className="required"></span></label>
                  <input
                    type="text"
                    name="businessOwnerName"
                    value={formData.businessOwnerName}
                    onChange={handleInputChange}
                    placeholder="Business Owner's Name"
                    readOnly={isReadOnly}
                  />
                  {errors.businessOwnerName && <span className="error-message">{errors.businessOwnerName}</span>}
                </div>
                <div className={`form-group ${focusedField === 'ownerContactNumber' ? 'focused' : ''}`}>
                  <label>Owner's Contact Number<span className="required"></span></label>
                  <PhoneInput
                    country={'in'}
                    value={formData.ownerContactNumber}
                    onChange={(value, data) => handlePhoneChange(value, data, 'ownerContactNumber')}
                    inputProps={{
                      name: 'ownerContactNumber',
                      required: true,
                      placeholder: 'Enter phone number',
                      readOnly: isReadOnly
                    }}
                    disabled={isReadOnly}
                  />
                  {errors.ownerContactNumber && <span className="error-message">{errors.ownerContactNumber}</span>}
                </div>
                <div className={`form-group ${focusedField === 'ownerEmail' ? 'focused' : ''}`}>
                  <label>Owner's Email ID<span className="required"></span></label>
                  <input
                    type="email"
                    name="ownerEmail"
                    value={formData.ownerEmail}
                    onChange={handleInputChange}
                    placeholder="Owner's Email ID"
                    required
                    readOnly={isReadOnly}
                  />
                  {errors.ownerEmail && <span className="error-message">{errors.ownerEmail}</span>}
                </div>
              </div>
            </section>

            <div className="form-actions">
              {!isReadOnly && (
                <button 
                  className="save-progress-button"
                  onClick={handleSaveProgress}
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting ? 'Saving...' : 'Save Progress'}
                </button>
              )}
              <div className="navigation-buttons">
                <button 
                  className="back-button"
                  onClick={handlePrevStep}
                  disabled={isSubmitting || isLoading}
                >
                  Back
                </button>
                <button 
                  className="next-button"
                  onClick={handleNext}
                  disabled={isSubmitting || isAnimating || isLoading}
                >
                  {isSubmitting ? 'Submitting...' : 'Next'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BasicInfoStep; 